import dayjs from 'dayjs';

import { HighScoreEntity, SingSetup, Song } from '~/interfaces';
import { Button } from '~/modules/elements/akui/button';
import { Icon } from '~/modules/elements/akui/icon';
import useKeyboardNav, { RegisterFunc } from '~/modules/hooks/use-keyboard-nav';
import ScoreboardPanel from '~/modules/scoreboard/scoreboard-panel';
import ScoreboardRow, { ScoreboardPlaceholderRow } from '~/modules/scoreboard/scoreboard-row';
import { useEditScore } from '~/modules/songs/stats/hooks';
import LeaderboardPrompt from '~/routes/game/singing/post-game/views/leaderboard/leaderboard-prompt';
import LeaderboardSharePanel from '~/routes/game/singing/post-game/views/leaderboard/leaderboard-share-panel';
import SongLeaderboardPanel from '~/routes/game/singing/post-game/views/leaderboard/song-leaderboard-panel';
import useLeaderboardPostGame from '~/routes/game/singing/post-game/views/leaderboard/use-leaderboard-post-game';

import HighScoreRename from './high-score-rename';

/**
 * Growing the boards is gated on the window being both wide enough for them to sit side by side and
 * tall enough to have height to spare — not on width alone, which is the trap here.
 *
 * A landscape phone is 844x390: wide enough for `md`, but with barely 300px for the step. Asking a
 * board to fill that while its list floors at 15rem gave the list a height its own panel could not
 * contain, and it burst out of the bottom. Dropping the floor instead let the list grow to every row
 * it had and stop scrolling. Neither is a board. Below this threshold nothing is overridden and the
 * panel keeps the fixed five-row list it has always had.
 */
/** The two boards, side by side from tablet up and stacked below it. */
const SCOREBOARD_CLASS =
  'border border-white/10 p-2 md:flex-1 md:basis-0 [@media(min-width:768px)_and_(min-height:720px)]:min-h-0';

/**
 * Where there is room, the boards take whatever height the step has spare rather than stopping at
 * the fixed five rows `ScoreboardPanel` defaults to. `min-h-60` is that default (15rem), kept as the
 * floor — safe here, because this only applies to a window with the height for it.
 *
 * Written out in full rather than built from a shared constant: Tailwind only generates classes it
 * finds spelled out in the source, so an interpolated variant would silently produce no CSS.
 */
const SCOREBOARD_LIST_CLASS =
  '[@media(min-width:768px)_and_(min-height:720px)]:h-auto [@media(min-width:768px)_and_(min-height:720px)]:min-h-60 [@media(min-width:768px)_and_(min-height:720px)]:flex-1';

/** How many rows the local board pads out to, so it stands as tall as the global one beside it. */
const LOCAL_SCOREBOARD_ROWS = 5;

interface Props {
  onNextStep: () => void;
  singSetup: SingSetup;
  highScores: HighScoreEntity[];
  song: Song;
}

function HighScoresView({ onNextStep, highScores, singSetup, song }: Props) {
  const { register } = useKeyboardNav({ title: 'High scores' });
  const editScore = useEditScore(song);
  const leaderboard = useLeaderboardPostGame({ song, singSetup });

  // An armed score goes up as the player moves on, so the wait is never in their way — the button
  // just holds them for as long as the request takes.
  const isArmed = leaderboard.panel === 'armed';

  const goToNextStep = async () => {
    if (isArmed) await leaderboard.share();

    onNextStep();
  };

  return (
    <>
      {/* Side by side from tablet up, stacked on a phone. Full width, so the pair lines up with the
          share panel and the tip under them rather than stopping short of both. */}
      <div className="flex flex-col items-start gap-3 md:flex-row md:items-stretch md:gap-6 [@media(min-width:768px)_and_(min-height:720px)]:min-h-0 [@media(min-width:768px)_and_(min-height:720px)]:flex-1">
        <ScoreboardPanel
          className={SCOREBOARD_CLASS}
          listClassName={SCOREBOARD_LIST_CLASS}
          title="Local scoreboard"
          subtitle="This song · this device"
          data-test="highscores-container">
          {highScores.map((score, index) => {
            const isCurrentRun = score.singSetupId === singSetup.id;

            return (
              <ScoreboardRow
                key={index}
                position={index + 1}
                score={score.score}
                highlighted={isCurrentRun}
                // The score just sung and its date are the only rows that change between runs — named
                // so the visual baselines can mask them and keep the seeded rows visible
                data-test={isCurrentRun ? 'highscore-current-row' : undefined}
                name={
                  isCurrentRun ? (
                    <HighScoreRename
                      index={index}
                      score={score}
                      register={register}
                      singSetupId={singSetup.id}
                      onSave={editScore}
                    />
                  ) : (
                    score.name
                  )
                }
                meta={dayjs(score.date).format('MMM D, YYYY')}
              />
            );
          })}
          {/* A device with fewer scores than this still stands as tall as the global board beside it */}
          {Array.from({ length: Math.max(0, LOCAL_SCOREBOARD_ROWS - highScores.length) }, (_, index) => (
            <ScoreboardPlaceholderRow key={`placeholder-${index}`} position={highScores.length + index + 1} />
          ))}
        </ScoreboardPanel>
        <SongLeaderboardPanel
          song={song}
          singSetup={singSetup}
          leaderboard={leaderboard}
          className={SCOREBOARD_CLASS}
          listClassName={SCOREBOARD_LIST_CLASS}
        />
      </div>
      <LeaderboardSharePanel register={register} leaderboard={leaderboard} />
      <div className="mt-auto">
        <SelectSongButton
          register={register}
          onClick={goToNextStep}
          label={isArmed ? 'Share score and sing a song' : 'Select next song'}
          isSubmitting={leaderboard.isSubmitting}
        />
      </div>
      <LeaderboardPrompt leaderboard={leaderboard} />
    </>
  );
}

interface SelectSongButtonProps {
  register: RegisterFunc;
  onClick: () => void;
  label: string;
  isSubmitting: boolean;
}

/**
 * Its own component so `register` runs during THIS render rather than the parent's — otherwise it
 * would be registered before the rename fields above it, putting it first in arrow navigation and in
 * the control list mirrored to the remote mic instead of last, where it appears on screen.
 */
function SelectSongButton({ register, onClick, label, isSubmitting }: SelectSongButtonProps) {
  return (
    <Button
      className="mt-2 w-full lg:mt-6 lg:ml-auto lg:w-5/12"
      // Kept off the register spread: a disabled registration returns no props at all, and the e2e
      // and the remote both need this button findable while the score is in flight.
      data-test="play-next-song-button"
      leftIcon={isSubmitting ? <Icon icon="ic:baseline-refresh" className="motion-safe:animate-spin" /> : undefined}
      {...register('play-next-song-button', onClick, undefined, true, {
        disabled: isSubmitting,
        control: { type: 'button', label },
      })}>
      {isSubmitting ? 'Sharing the score…' : label}
    </Button>
  );
}

export default HighScoresView;
